import { ServiceItem, ServiceTransaction } from '../types';
import { supabase } from './supabaseClient';
import { countLinkedChildren } from './cloudService';
import { fetchLinkedChildren } from './projectLinkService';
import { bestandteilFelderAusZeile } from './projectLv';
import {
    ArchivUrteil,
    BELEG_OFFEN_STATUS,
    BelegZustand,
    EigenePosition,
    TochterZustand,
    beurteileArchivierung,
    brauchtEnkelfrage,
    verdichteEigenePosten,
    verdichtePositionen,
} from './archiveGuardLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER ARCHIV-RIEGEL — die Beschaffung
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die REGEL steht in `archiveGuardLogic` und ist ohne Supabase pruefbar; hier
 * wird nur beschafft, was sie braucht. Dieselbe Aufteilung wie bei
 * `unbookedReportsLogic` (Regel) und `projectQuietService` (Beschaffung).
 *
 * Gefragt wird in zwei Straengen, und jeder Schritt kann NUR sperren.
 *
 * STRANG A — die eigenen offenen Posten (Regel 2, gilt fuer jedes Projekt):
 *   (1) Welche Leistungspositionen fuehrt das Projekt SELBST?
 *   (2) Was steht in seinem Leistungsprotokoll (fuer die Alt-Deckung)?
 *
 * STRANG B — die Toechter (Regel 1, gilt nur fuer Muetter):
 *   (3) Welche Toechter haengen an diesem Projekt? Dieselbe Abfrage wie in
 *       `countLinkedChildren` und `useProjectHubData` — die Verknuepfung steht
 *       im JSON der TOCHTER, nicht an der Mutter.
 *   (4) Welche eigenen Positionen fuehren diese Toechter noch? (Der Regelfall
 *       ist: keine — siehe die lange Begruendung in `archiveGuardLogic`.)
 *   (5) Haengen an einer archivreifen Tochter selbst wieder Toechter?
 *
 * Faellt irgendetwas davon aus, ist das Ergebnis `null` bzw. `undefined` und
 * die Regel sperrt. Es wird NIE geworfen: der Riegel darf keine Ansicht zum
 * Absturz bringen, aber er darf auch nicht still durchlassen.
 */

/** PostgREST kappt eine `in`-Liste in der URL — deshalb in Haeppchen. */
const HAPPEN = 200;
/** Angefragte Seitengroesse beim Blaettern; liefert PostgREST weniger (max-rows), geht es trotzdem weiter. */
const BELEG_SEITE = 1000;

interface TochterZeile {
    id: string;
    name?: string | null;
    project_number?: string | null;
    status?: string | null;
    jsonStatus?: string | null;
}

/**
 * Die direkten Toechter eines Projekts — oder `null`, wenn das nicht
 * feststellbar war.
 *
 * Die Verknuepfung steht seit Nr. 71 an der Tochter in
 * `projects.parent_project_id`, nicht an der Mutter. Es gibt deshalb keine
 * zweite Quelle, gegen die sich eine unvollstaendige Antwort gegenpruefen
 * liesse: was die RLS nicht herausgibt, ist fuer diesen Riegel unsichtbar.
 * Genau deshalb sperrt hier schon der blosse Abfragefehler — auch der, dass
 * es die Spalte in dieser Datenbank noch nicht gibt.
 */
const ladeToechter = async (projectId: string): Promise<TochterZeile[] | null> => {
    try {
        const data = await fetchLinkedChildren<TochterZeile>(
            projectId,
            'id, name, project_number, status, jsonStatus:data->metadata->globalData->>status',
        );
        return data;
    } catch {
        return null;
    }
};

/**
 * Die eigenen Positionen der Toechter, je Projekt-Id.
 *
 * `null` heisst „nicht feststellbar" und faellt in der Regel auf
 * `ausgeglichen: null` durch — also auf sperren. Eine Tochter OHNE Zeilen ist
 * dagegen etwas anderes und voellig normal: ihr LV liegt an der Mutter.
 */
/**
 * Eine eigene Position einer Tochter, so weit der Riegel sie liest. Die
 * Bestandteil-Felder (Nr. 165/166) gehen mit, obwohl `verdichtePositionen`
 * heute nur die Mengen vergleicht: eine gebuchte, aber gesperrte
 * Paketposition (erbracht, nicht abgerechnet) sperrt schon ueber die Mengen,
 * und wer die Regel um das Gate erweitert, soll es nicht erst beschaffen
 * muessen.
 */
type TochterPosition = { quantityUsed: number; quantityBilled: number; quantityWaived: number }
    & Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen'>;

const ladeEigenePositionen = async (
    ids: string[],
): Promise<Map<string, TochterPosition[]> | null> => {
    const jeProjekt = new Map<string, TochterPosition[]>();
    for (const id of ids) jeProjekt.set(id, []);
    try {
        for (let i = 0; i < ids.length; i += HAPPEN) {
            // Namentliche Liste mit den beiden Spalten aus Nr. 165: fehlen sie
            // (SQL nicht gefahren), scheitert die Abfrage mit 42703 und der
            // Riegel SPERRT (`null` → „nicht feststellbar"). Das ist die
            // sichere Richtung, und Nr. 165 laeuft vor dem Client-Deploy.
            // Dasselbe gilt fuer die beiden Stunden-Spalten aus Nr. 171 — sie
            // stehen hier, weil jede Mengen-Liste die ganze Paketposition
            // liest (bestandteileSpaltenWaechter.test.ts); die Regel braucht
            // sie nicht, Nr. 171 laeuft ebenfalls vor dem Client-Deploy.
            const { data, error } = await supabase
                .from('project_services')
                .select('project_id, quantity_used, quantity_billed, quantity_waived, bestandteile, bestandteile_offen, stunden_kontingent, stunden_erfasst')
                .in('project_id', ids.slice(i, i + HAPPEN));
            if (error) return null;
            for (const s of data || []) {
                jeProjekt.get(s.project_id as string)?.push({
                    quantityUsed: Number(s.quantity_used) || 0,
                    quantityBilled: Number(s.quantity_billed) || 0,
                    quantityWaived: Number(s.quantity_waived) || 0,
                    ...bestandteilFelderAusZeile(s),
                });
            }
        }
        return jeProjekt;
    } catch {
        return null;
    }
};

/**
 * Der Zustand aller Toechter eines Projekts — die Eingabe der Regel.
 *
 * `null` heisst „ueberhaupt nicht feststellbar"; ein leeres Array heisst „es
 * gibt keine Toechter". Der Unterschied entscheidet ueber erlaubt und
 * gesperrt und darf nirgends eingeebnet werden.
 */
export const ladeTochterZustaende = async (
    projectId: string,
): Promise<TochterZustand[] | null> => {
    const id = (projectId || '').trim();
    // Ein noch nie gespeichertes Projekt kann keine Toechter haben: die
    // Verknuepfung zeigt auf eine Datenbank-Id, die es noch nicht gibt.
    if (!id || id === 'temp-id') return [];

    const zeilen = await ladeToechter(id);
    if (zeilen === null) return null;
    if (zeilen.length === 0) return [];

    const positionen = await ladeEigenePositionen(zeilen.map((z) => z.id));

    const zustaende: TochterZustand[] = [];
    for (const z of zeilen) {
        const eigene = positionen?.get(z.id);
        const verdichtet = eigene ? verdichtePositionen(eigene) : null;
        const zustand: TochterZustand = {
            projectId: z.id,
            projectNumber: z.project_number ?? null,
            name: z.name ?? null,
            statusSpalte: z.status ?? null,
            statusJson: z.jsonStatus ?? null,
            ausgeglichen: verdichtet ? verdichtet.ausgeglichen : null,
            offenePositionen: verdichtet ? verdichtet.offenePositionen : null,
        };

        // Die Enkel-Frage kostet je Tochter eine eigene Abfrage (die
        // Verknuepfung ist nicht in einem `in` zu haben). Ob sie noch etwas
        // entscheiden kann, sagt die REGEL selbst (`brauchtEnkelfrage`) — eine
        // eigene, strengere Bedingung hier liefe gegen `effektiverStatus`
        // auseinander und blockierte Altzeilen mit nur einer Statusquelle
        // grundlos als „unbekannt".
        if (brauchtEnkelfrage(zustand)) {
            zustand.eigeneToechter = await countLinkedChildren(z.id);
        }

        zustaende.push(zustand);
    }
    return zustaende;
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE EIGENEN OFFENEN POSTEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die eigenen Leistungspositionen — oder `null`, wenn das nicht feststellbar
 * war.
 *
 * ─── Warum `select('*')` und keine Spaltenliste ────────────────────────────
 * Die Groesse braucht `quantity_waived` (Nr. 50) und `flat_rate` (Nr. 53).
 * Eine NAMENTLICHE Liste mit diesen Spalten ist genau die Falle, die am
 * 02.09.2026 die Projektliste flottenweit lahmgelegt hat: fehlt die Spalte,
 * antwortet PostgREST mit 42703 — und hier hiesse das nicht „Anzeige aermer",
 * sondern „ab sofort ist NICHTS mehr archivierbar". Ein `*` kann 42703 nicht
 * ausloesen (PostgREST liefert, was da ist), und der Preis dafuer sind ein
 * paar Spalten mehr an einer Abfrage, die nur beim Archivieren laeuft.
 * Derselbe Grund, aus dem `loadProjectById` mit `*` liest (siehe den
 * Nachweis in spaltenRueckfall.ts).
 *
 * Fehlt eine Spalte trotzdem, ist ihr Wert `undefined` und die Position
 * verhaelt sich wie vor der jeweiligen Migration: ohne `quantity_waived`
 * zaehlt der Verzicht nicht (der Riegel wird STRENGER, nicht laxer), ohne
 * `flat_rate` gibt es keine Pauschale (ebenfalls strenger). Beides ist die
 * sichere Richtung.
 */
const ladeEigeneServices = async (projectId: string): Promise<ServiceItem[] | null> => {
    try {
        const { data, error } = await supabase
            .from('project_services')
            .select('*')
            .eq('project_id', projectId);
        if (error) return null;
        if (!Array.isArray(data)) return null;
        return data.map((s: Record<string, unknown>) => ({
            id: String(s.id),
            lvPosition: (s.lv_position as string) ?? '',
            title: (s.title as string) ?? '',
            unit: (s.unit as string) ?? '',
            quantityContracted: Number(s.quantity_contracted) || 0,
            quantityUsed: Number(s.quantity_used) || 0,
            quantityBilled: Number(s.quantity_billed) || 0,
            unitPrice: Number(s.unit_price) || 0,
            status: (s.status as ServiceItem['status']) ?? 'open',
            assignment: s.assignment as ServiceItem['assignment'],
            // NEP muss mit: sonst hielte projectAutoArchive eine unbestellte
            // Eventualmenge fuer „noch beauftragt" und das Projekt bliebe offen.
            ...(s.is_optional === true ? { is_optional: true } : {}),
            quantityWaived: s.quantity_waived == null ? undefined : Number(s.quantity_waived),
            flatRate: (s.flat_rate as ServiceItem['flatRate']) ?? undefined,
            // Paketposition (Nr. 165/166): ohne Definition und Gate saehe der
            // Riegel eine Paketposition als gewoehnliche. Fehlt die Spalte
            // (`*` liefert, was da ist), bleiben die Felder weg — dieselbe
            // Lesart wie in jedem anderen Ladeweg.
            ...bestandteilFelderAusZeile(s),
        }));
    } catch {
        return null;
    }
};

/**
 * Der JSON-Zweig des Projekts, aus dem zwei Dinge kommen:
 *   • `serviceLog` — die Grundlage der Alt-Deckung (`ALT-`-Buchungen).
 *   • `services` — der RUECKFALL fuer Projekte ohne relationale Positionen.
 *     `getNormalizedServices` (projectLv.ts) faellt genauso zurueck, und ein
 *     Riegel, der eine andere Positionsliste sieht als die
 *     Buchhaltungsansicht, streitet mit dem Anwender ueber die Zahlen.
 *
 * Gelesen wird ueber JSON-PFADE auf `data`, nicht ueber `data` selbst: der
 * Blob traegt Fotos und ganze Berichtsstaende, und davon wird hier nichts
 * gebraucht. `data` gibt es in jeder Umgebung — ein 42703 ist ausgeschlossen.
 *
 * Das Protokoll wird bewusst aus dem JSON gelesen und nicht aus
 * `service_journal`: das Journal ist die Zweitschrift (Nr. 59-61), das JSON
 * bleibt die Quelle, aus der auch `getServiceLog` ohne Journalzeilen liest.
 */
const ladeEigenenJsonZweig = async (
    projectId: string,
): Promise<{ log: ServiceTransaction[]; services: ServiceItem[] } | null> => {
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('log:data->metadata->globalData->serviceLog, services:data->metadata->globalData->services')
            .eq('id', projectId)
            .maybeSingle();
        if (error) return null;
        if (!data) return null;
        const roh = data as unknown as { log?: unknown; services?: unknown };
        return {
            log: Array.isArray(roh.log) ? (roh.log as ServiceTransaction[]) : [],
            services: Array.isArray(roh.services) ? (roh.services as ServiceItem[]) : [],
        };
    } catch {
        return null;
    }
};

/**
 * Die eigenen offenen Posten — die Eingabe der zweiten Regel.
 *
 * `null` heisst „nicht feststellbar" und sperrt; ein leeres Array heisst
 * „geprueft, nichts offen". Derselbe Unterschied wie bei den Toechtern, und
 * aus demselben Grund.
 */
export const ladeEigeneOffenePosten = async (
    projectId: string,
): Promise<EigenePosition[] | null> => {
    const id = (projectId || '').trim();
    // Ein noch nie gespeichertes Projekt hat weder relationale Positionen noch
    // eine Zeile in `projects` — und damit auch nichts Offenes.
    if (!id || id === 'temp-id') return [];

    const [relational, json] = await Promise.all([
        ladeEigeneServices(id),
        ladeEigenenJsonZweig(id),
    ]);
    if (relational === null || json === null) return null;

    // Dieselbe Vorrangregel wie `getNormalizedServices`: die relationale Liste
    // gewinnt, das JSON ist der Rueckfall fuer Projekte, deren Positionen nie
    // relational angelegt wurden.
    const services = relational.length > 0 ? relational : json.services;
    return verdichteEigenePosten(services, json.log);
};

/**
 * REGEL 3 — die Belege des Projekts aus dem Rechnungsmodul.
 *
 * Nur die vier Felder, die das Urteil braucht; `primary_project_id` ist
 * dieselbe Verknuepfung wie in `fetchInvoicesForProject`. Scheitert die
 * Abfrage, kommt `null` zurueck und das Urteil sperrt — nie ein leeres Array,
 * denn das hiesse „geprueft, keine Belege" und liesse durch.
 */
const ladeBelegZustaende = async (projectId: string): Promise<BelegZustand[] | null> => {
    const id = (projectId || '').trim();
    if (!id || id === 'temp-id') return [];
    try {
        const { data, error } = await supabase
            .from('invoices')
            .select('id, invoice_number, document_type, status')
            .eq('primary_project_id', id);
        if (error) return null;
        return (data || []).map((row: any) => ({
            id: String(row.id),
            invoiceNumber: row.invoice_number ?? null,
            documentType: row.document_type ?? null,
            status: row.status ?? null,
        }));
    } catch {
        return null;
    }
};

/**
 * Dieselben Belegzustaende fuer viele Projekte auf einmal — fuer die
 * Abrechnungs-Uebersicht, die bei jedem BEENDETEN Projekt den Stand der
 * Schlussrechnung zeigt. Jedes angefragte Projekt bekommt einen Eintrag (auch
 * ein leeres Array). Scheitert eine Abfrage, kommt `null` zurueck: dann ist
 * der Stand fuer ALLE unbekannt, nicht stillschweigend „keine Belege".
 */
export const ladeBelegZustaendeJeProjekt = async (
    projectIds: string[],
): Promise<Map<string, BelegZustand[]> | null> => {
    const ids = [...new Set(projectIds.map((id) => (id || '').trim()).filter((id) => id && id !== 'temp-id'))];
    const jeProjekt = new Map<string, BelegZustand[]>();
    for (const id of ids) jeProjekt.set(id, []);
    try {
        for (let i = 0; i < ids.length; i += HAPPEN) {
            // Die Haeppchen begrenzen die URL, nicht die ANTWORT: 200 Projekte
            // koennen mehr Belege haben, als PostgREST in einer Seite liefert
            // (max-rows). Ohne Blaettern fehlte dann z. B. eine unbezahlte
            // Schlussrechnung still, und die Liste zeigte „bezahlt". Weiter um
            // die GELIEFERTE Zahl und Schluss erst bei einer leeren Seite: so
            // geht auch bei einem kleineren max-rows keine Zeile verloren.
            for (let von = 0; ;) {
                const { data, error } = await supabase
                    .from('invoices')
                    .select('id, primary_project_id, invoice_number, document_type, status')
                    .in('primary_project_id', ids.slice(i, i + HAPPEN))
                    .order('id', { ascending: true })
                    .range(von, von + BELEG_SEITE - 1);
                if (error) return null;
                for (const row of data || []) {
                    jeProjekt.get(String(row.primary_project_id))?.push({
                        id: String(row.id),
                        invoiceNumber: row.invoice_number ?? null,
                        documentType: row.document_type ?? null,
                        status: row.status ?? null,
                    });
                }
                if (!data || data.length === 0) break;
                von += data.length;
            }
        }
        return jeProjekt;
    } catch {
        return null;
    }
};

/**
 * Alle OFFENEN Belege der Flotte, je Projekt — fuer ARCHIVIERTE Projekte mit
 * unbezahlter Rechnung (`offenerBelegStand`). Projektverwaltung und
 * Abrechnungs-Uebersicht lassen so ein Archiv als „Archiv (offen)" stehen.
 *
 * EINE Abfrage ueber den Status statt je Projekt: offene Belege sind wenige,
 * archivierte Projekte viele. Verknuepft ueber `primary_project_id` wie Regel 3
 * (`ladeBelegZustaende`); eine Sammelrechnung ohne Hauptprojekt zaehlt dort
 * wie hier nicht. Geblaettert wie oben. `null` = nicht ladbar: die Listen
 * verhalten sich dann wie vor dieser Regel.
 */
export const ladeOffeneBelegeJeProjekt = async (): Promise<Map<string, BelegZustand[]> | null> => {
    const jeProjekt = new Map<string, BelegZustand[]>();
    try {
        for (let von = 0; ;) {
            const { data, error } = await supabase
                .from('invoices')
                .select('id, primary_project_id, invoice_number, document_type, status')
                .in('status', [...BELEG_OFFEN_STATUS])
                .not('primary_project_id', 'is', null)
                .order('id', { ascending: true })
                .range(von, von + BELEG_SEITE - 1);
            if (error) return null;
            for (const row of data || []) {
                const projektId = String(row.primary_project_id);
                const liste = jeProjekt.get(projektId) ?? [];
                liste.push({
                    id: String(row.id),
                    invoiceNumber: row.invoice_number ?? null,
                    documentType: row.document_type ?? null,
                    status: row.status ?? null,
                });
                jeProjekt.set(projektId, liste);
            }
            if (!data || data.length === 0) break;
            von += data.length;
        }
        return jeProjekt;
    } catch {
        return null;
    }
};

/**
 * DER RIEGEL: darf dieses Projekt ins Archiv?
 *
 * Alle DREI Regeln: eigene Posten, Belege (Schlussrechnung gestellt und
 * bezahlt), Toechter. Das ist der Weg des Administrators — ProjectStats und
 * AdminProjectList. Der Auto-Abschluss geht ueber
 * `pruefeArchivierbarkeitFuerStand` und laesst Regel 3 aus, weil er nur auf
 * BEENDET setzt.
 *
 * Muss VOR jedem Nebeneffekt des Archivierens laufen — insbesondere vor der
 * Rueckfrage zur Dokumentenablage, an der das LOESCHEN von Dateien haengt.
 * Ein Riegel hinter einem Storage-Aufruf kommt zu spaet; derselbe Grund, aus
 * dem der `isReadOnly`-Riegel in `ProjectStats` nach vorn gezogen wurde.
 *
 * Beide Regeln werden PARALLEL beschafft: sie haengen nicht voneinander ab,
 * und der Anwender wartet an dieser Stelle mit einem Spinner. Beide Ergebnisse
 * gehen in EIN Urteil — wer zwei Absagen hintereinander bekommt, klickt beim
 * zweiten Mal an derselben Wand.
 */
export const pruefeArchivierbarkeit = async (projectId: string): Promise<ArchivUrteil> => {
    const [eigenePosten, toechter, belege] = await Promise.all([
        ladeEigeneOffenePosten(projectId),
        ladeTochterZustaende(projectId),
        ladeBelegZustaende(projectId),
    ]);
    return beurteileArchivierung({ eigenePosten, toechter: toechter ?? null, belege });
};

/**
 * DER RIEGEL fuer Aufrufer, die den EIGENEN Stand schon in der Hand halten.
 *
 * OHNE Regel 3: dieser Weg setzt ein Projekt auf BEENDET ('completed'), nicht
 * ins Archiv — und BEENDET ist gerade der Zustand, in dem die Schlussrechnung
 * erst noch geschrieben wird. Eine bezahlte Schlussrechnung zu verlangen
 * hiesse, den Automatismus nie greifen zu lassen.
 *
 * `withAutoArchive` (manualBillingService / serviceReversalService /
 * serviceWaiverService) laeuft VOR `saveProjectToCloud`: die Datenbank traegt
 * in diesem Moment noch den Stand von VOR dem Vorgang. Wer hier ueber
 * `pruefeArchivierbarkeit` aus der Datenbank laese, saehe die soeben
 * geschlossene Menge noch als offen — und der Automatismus verweigerte das
 * Archiv ausgerechnet in dem Moment, fuer den es ihn gibt, mit einer Meldung,
 * die dem Anwender die gerade erledigte Position als offen vorhaelt.
 *
 * Deshalb kommt der eigene Strang aus dem uebergebenen Stand (den Positionen
 * und dem Protokoll, die der Aufrufer gleich SPEICHERT — die frischeste
 * Wahrheit, die es gibt), und nur die Toechter kommen aus der Datenbank: an
 * deren Zeilen aendert der Vorgang nichts. Die Sperr-Richtung bleibt gleich —
 * scheitert die Toechter-Abfrage, sperrt das Urteil.
 */
export const pruefeArchivierbarkeitFuerStand = async (
    projectId: string,
    services: ServiceItem[] | null | undefined,
    serviceLog: ServiceTransaction[] | null | undefined,
): Promise<ArchivUrteil> => {
    const toechter = await ladeTochterZustaende(projectId);
    return beurteileArchivierung({
        eigenePosten: verdichteEigenePosten(services, serviceLog),
        toechter: toechter ?? null,
    });
};
